import { UploadOutlined } from '@ant-design/icons';
import { Button, message, UploadFile } from 'antd';
import { AuthResult, AzureBlobResponse, AzureUpload } from './azure-upload';
import { FileValidator } from './file-validator';
import { RcFile } from 'antd/lib/upload';

interface FileUploadButtonProps {
  authorizeRequest: (file: File) => Promise<AuthResult>;
  onChange?: (value: any) => void;
  onRemoveRequested?: () => Promise<boolean>;
  onInvalidContentType?: () => void;
  onInvalidContentLength?: () => void;
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  maxFileSizeBytes?: number;
  maxWidthOrHeight?: number;
  onSuccess?: (response: AzureBlobResponse, file: UploadFile) => Promise<void>;
  onError?: () => void;
  button?: React.ReactNode;
  maxFileCount?: number;
  buttonText?: string;
  isIcon?: boolean;
  isUploadPreviewImage?: boolean;
  isProcessing?: boolean; // is processing uploading file
  caseState?: string;
  isCurrentAssigned?: boolean;
  isProcessingGeneratingPdf?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  cropperProps?: object;
  isDisabled?: boolean;
}

export const FileUploadButton: React.FC<FileUploadButtonProps> = ({ isIcon = false, isUploadPreviewImage: uploadImage = true, ...props }) => {
  const handleOnSuccess = async (response: AzureBlobResponse, file: UploadFile) => {
    if (props.onSuccess) {
      await props.onSuccess(response, file);
      props.setIsEditing && props.setIsEditing(false);
    }
  };

  const isButtonDisabled = (): boolean => {

    if (props.caseState && ((props.caseState === 'draft' || props.caseState === "decisionRendered") || !props.isCurrentAssigned || (props.caseState === "revisionRequested" && props.isCurrentAssigned))) {
      return true;
    }

    return false;
  };

  const beforeCrop = async (file: RcFile) => {
    const validator = new FileValidator(file, {
      permittedContentTypes: props.permittedContentTypes,
      maxFileSizeBytes: props.maxFileSizeBytes,
      maxWidthOrHeight: props.maxWidthOrHeight
    });

    const result = await validator.validate();
    if (!result.success || file.type === 'application/pdf') {
      return false;
    }

    return true;
  };

  return (
    <AzureUpload
      data={{
        permittedContentTypes: props.permittedContentTypes,
        permittedExtensions: props.permittedExtensions,
        maxFileSizeBytes: props.maxFileSizeBytes,
        maxWidthOrHeight: props.maxWidthOrHeight
      }}
      authorizeRequest={props.authorizeRequest}
      onInvalidContentType={
        props.onInvalidContentType
          ? props.onInvalidContentType
          : () => {
              message.error('Files of that content type are not permitted');
            }
      }
      onInvalidContentLength={
        props.onInvalidContentLength
          ? props.onInvalidContentLength
          : () => {
              message.error('PDF File Size should not exceed 10MB');
            }
      }
      onRemoveRequested={props.onRemoveRequested}
      onSuccess={handleOnSuccess}
      onError={() => {
        props.onError && props.onError();
      }}
      uploadProps={{
        onChange: props.onChange
      }}
      maxFileCount={props.maxFileCount}
      buttonText={props.buttonText}
      uploadPreviewImage={uploadImage}
      cropperProps={{...props.cropperProps, beforeCrop}}
    >
      <Button type="primary" disabled={isButtonDisabled() || props.isProcessingGeneratingPdf || props.isDisabled} loading={props.isProcessing} style={{ marginTop: '10px', minWidth: '160px'}}>
        {isIcon && <UploadOutlined />} {props.buttonText ? props.buttonText : 'Upload File'}
      </Button>
    </AzureUpload>
  );
};
